import { WorkspaceDatabaseKeys } from '@database/workspace';
import { ErrorCodes, ReferenceTableModel } from '@shared';
import { firstValueFrom, iif, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DeleteIntervalService } from '../services';
import { ValidationKeys } from '../../../../dynamic-form/validations/models';
import { DeleteChainParams, IDeleteValidatorHandler, IValidationsRule } from './models';
import { LoggingService } from '@shared/logging';
import { BusinessLayerService, SampleTypeProvider, TableTypeProvider } from '../../../services/';

export class OriginalHasQcCheck implements IDeleteValidatorHandler {
	private next: IDeleteValidatorHandler;

	public constructor(
		private deleteDataService: DeleteIntervalService,
		private loggerService: LoggingService,
		private businessLayerService: BusinessLayerService,
		private sampleTypeProvider: SampleTypeProvider,
		private tableTypeProvider: TableTypeProvider
	) {}

	public setNext(nextHandler: IDeleteValidatorHandler) {
		this.next = nextHandler;
	}

	public async handle(deleteChainParams: DeleteChainParams): Promise<IValidationsRule | null> {
		try {
			const originalHasQcValidation: IValidationsRule | null = await firstValueFrom(
				this.tableTypeProvider.isSampleTable$(deleteChainParams.tableName.toLowerCase()).pipe(
					switchMap((isSampleTable: boolean) =>
						iif(
							() => isSampleTable,
							this.sampleTypeProvider.getGroupedByCategorySampleTypes$().pipe(
								map((groupedRefTables: { [key: string]: ReferenceTableModel[] } | null) => {
									const originalTypes = groupedRefTables[
										WorkspaceDatabaseKeys.referenceTables.originalCategory
									].map(
										(refTable: ReferenceTableModel) =>
											refTable[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode]
									);

									if (!originalTypes.includes(deleteChainParams.sampleType)) {
										return null;
									}

									if (!groupedRefTables[WorkspaceDatabaseKeys.referenceTables.qcCategory]) {
										return null;
									}

									return groupedRefTables[WorkspaceDatabaseKeys.referenceTables.qcCategory];
								}),
								map((qcRefTypes: ReferenceTableModel[]) =>
									qcRefTypes
										? qcRefTypes.map(
												(refTable: ReferenceTableModel) =>
													refTable[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode]
										  )
										: null
								),
								switchMap((qcRefTypes: string[] | null) =>
									qcRefTypes
										? this.deleteDataService
												.originalHasQc$(
													deleteChainParams.tableName,
													deleteChainParams.workfileId,
													deleteChainParams.projectNumber,
													deleteChainParams.entityName,
													deleteChainParams.entryId,
													deleteChainParams.childEntryId,
													deleteChainParams.depthFrom,
													deleteChainParams.depthTo,
													qcRefTypes
												)
												.pipe(
													map((res: boolean) =>
														res
															? {
																	validationKey:
																		ValidationKeys.validationRules.originalHasQcRule
															  }
															: null
													)
												)
										: of(null)
								)
							),
							of(null)
						)
					)
				)
			);

			if (originalHasQcValidation) {
				return originalHasQcValidation;
			}

			if (this.next) {
				return await this.next.handle(deleteChainParams);
			}

			return null;
		} catch (error) {
			this.loggerService.error(ErrorCodes.OriginalHasQcError, error);
			throw error;
		}
	}
}
